import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso900114001DebouncedValue, useIso900114001LatestRequest } from "../../../components/shared/useIso900114001FilteredRequest";
import { getCalidadDafo } from "../api/dafo";
import type { DafoFilters, DafoResponse } from "../types/dafo";

const emptyResponse: DafoResponse = {
  analisis: [],
  partes_interesadas: [],
  control_modificaciones: [],
  partes_resumen: [],
  meta: {
    years: [],
    current_year: new Date().getFullYear(),
    default_source_year: null,
    suggested_new_year: new Date().getFullYear(),
    analisis_total: 0,
    partes_interesadas_total: 0,
  },
};

export function useCalidadDafo(filters: DafoFilters, enabled = true) {
  const [data, setData] = useState<DafoResponse>(emptyResponse);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso900114001LatestRequest();
  const requestFilters = useMemo<DafoFilters>(() => ({
    ...filters,
    search: debouncedSearch,
  }), [debouncedSearch, filters.anio, filters.parte_interesada]);
  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  const reload = useCallback(async (force = true) => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => getCalidadDafo(requestFilters, signal), force);
      if (response) setData(response);
    } catch {
      setError("No se pudieron cargar los datos DAFO.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [enabled, hasActiveRequest, requestFilters, requestKey, runLatest]);

  useEffect(() => { void reload(false); }, [reload]);

  return { ...data, loading, error, reload };
}
